import React, { useContext, useEffect, useMemo } from "react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { API_BASE_URL } from "../config/api";

import { ProductsContext } from "../context/ProductsContext";
import { PartsContext } from "../context/PartsContext";
import { useLanguage } from "../context/LanguageContext";

/* =====================================================
   API
===================================================== */

const FALLBACK_IMAGE =
  "https://placehold.co/400x400?text=No+Image";

const VISITOR_KEY = "masterSolVisitorId";

const getVisitorId = () => {
  let visitorId = localStorage.getItem(VISITOR_KEY);

  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 10)}`;

    localStorage.setItem(VISITOR_KEY, visitorId);
  }

  return visitorId;
};

/* =====================================================
   SEARCH PAGE
===================================================== */

const Search = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { lang } = useLanguage();

  /* =====================================================
     CONTEXTS
  ===================================================== */

  const productsContext = useContext(ProductsContext);
  const partsContext = useContext(PartsContext);

  /*
   * Funksionon në të dyja rastet:
   *
   * 1. Context kthen direkt array
   * 2. Context kthen objekt:
   *    { products: [...] }
   *    { parts: [...] }
   */

  const products = Array.isArray(productsContext)
    ? productsContext
    : productsContext?.products || [];

  const parts = Array.isArray(partsContext)
    ? partsContext
    : partsContext?.parts || [];

  /* =====================================================
     TRANSLATIONS
  ===================================================== */

  const translations = {
    sq: {
      title: "Rezultatet e kërkimit",
      resultsFor: "Rezultatet për",
      results: "rezultate",

      products: "Produkte",
      parts: "Pjesë këmbimi",

      noResults: "Nuk u gjet asnjë produkt ose pjesë për",

      viewProduct: "Shiko produktin",
      viewPart: "Shiko pjesën",

      unknownProduct: "Produkt",
      unknownPart: "Pjesë këmbimi",
    },

    en: {
      title: "Search Results",
      resultsFor: "Results for",
      results: "results",

      products: "Products",
      parts: "Spare Parts",

      noResults:
        "No products or spare parts were found for",

      viewProduct: "View product",
      viewPart: "View part",

      unknownProduct: "Product",
      unknownPart: "Spare part",
    },
  };

  const text = translations[lang] || translations.sq;

  /* =====================================================
     SEARCH KEYWORD
  ===================================================== */

  const originalKeyword = searchParams.get("q") || "";

  const normalizeText = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const keyword = normalizeText(originalKeyword);

  /* =====================================================
     SEARCH TRACKING
  ===================================================== */

  useEffect(() => {
    if (!originalKeyword.trim()) return;

    const trackSearch = async () => {
      try {
        const visitorId = getVisitorId();

        await fetch(
          `${API_BASE_URL}/api/search-logs/track`,
          {
            method: "POST",

            headers: {
              "Content-Type": "application/json",
            },

            body: JSON.stringify({
              visitor_id: visitorId,
              search_term: originalKeyword.trim(),
              product_id: null,
            }),
          }
        );
      } catch (error) {
        console.error(
          "Search tracking error:",
          error
        );
      }
    };

    trackSearch();
  }, [originalKeyword]);

  /* =====================================================
     PRODUCT FILTER
  ===================================================== */

  const productResults = useMemo(() => {
    if (!keyword) return [];

    return products.filter((product) => {
      const searchableText = normalizeText(
        [
          product.name,
          product.brand,
          product.model,
          product.category,
          product.description,
          product.color,
          product.memory,
          product.product_condition,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return searchableText.includes(keyword);
    });
  }, [products, keyword]);

  /* =====================================================
     PART FILTER
  ===================================================== */

  const partResults = useMemo(() => {
    if (!keyword) return [];

    return parts.filter((part) => {
      const searchableText = normalizeText(
        [
          part.name,
          part.brand,
          part.model,
          part.category,
          part.description,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return searchableText.includes(keyword);
    });
  }, [parts, keyword]);

  const totalResults =
    productResults.length + partResults.length;

  /* =====================================================
     IMAGE HELPERS
  ===================================================== */

  const createImageUrl = (imagePath) => {
    if (!imagePath || typeof imagePath !== "string") {
      return FALLBACK_IMAGE;
    }

    const cleanPath = imagePath.trim();

    if (!cleanPath) {
      return FALLBACK_IMAGE;
    }

    /*
     * URL e plotë
     */
    if (
      cleanPath.startsWith("http://") ||
      cleanPath.startsWith("https://") ||
      cleanPath.startsWith("data:") ||
      cleanPath.startsWith("blob:")
    ) {
      return cleanPath;
    }

    /*
     * Shembull:
     * /uploads/iphone.jpg
     */
    if (cleanPath.startsWith("/uploads/")) {
      return `${API_BASE_URL}${cleanPath}`;
    }

    /*
     * Shembull:
     * uploads/iphone.jpg
     */
    if (cleanPath.startsWith("uploads/")) {
      return `${API_BASE_URL}/${cleanPath}`;
    }

    /*
     * Shembull:
     * iphone.jpg
     */
    return `${API_BASE_URL}/uploads/${cleanPath.replace(
      /^\/+/,
      ""
    )}`;
  };

  const extractFirstImage = (images) => {
    if (!images) return "";

    /*
     * Array:
     * ["iphone.jpg", "iphone2.jpg"]
     */
    if (Array.isArray(images)) {
      return images[0] || "";
    }

    if (typeof images !== "string") {
      return "";
    }

    const cleanValue = images.trim();

    if (!cleanValue) return "";

    /*
     * JSON string:
     * '["iphone.jpg","iphone2.jpg"]'
     */
    if (
      cleanValue.startsWith("[") &&
      cleanValue.endsWith("]")
    ) {
      try {
        const parsedImages = JSON.parse(cleanValue);

        if (Array.isArray(parsedImages)) {
          return parsedImages[0] || "";
        }
      } catch (error) {
        console.warn(
          "Invalid images JSON:",
          cleanValue
        );
      }
    }

    /*
     * Comma-separated string:
     * iphone.jpg,iphone2.jpg
     */
    return (
      cleanValue
        .split(",")
        .map((image) => image.trim())
        .filter(Boolean)[0] || ""
    );
  };

  const getProductImage = (product) => {
    if (!product) return FALLBACK_IMAGE;

    const image =
      product.image ||
      extractFirstImage(product.images);

    return createImageUrl(image);
  };

  const getPartImage = (part) => {
    if (!part) return FALLBACK_IMAGE;

    const image =
      part.image ||
      extractFirstImage(part.images);

    return createImageUrl(image);
  };

  /* =====================================================
     NAVIGATION
  ===================================================== */

const openProduct = (product) => {
  const brand = String(product?.brand || "").trim();
  const model = String(
    product?.model ||
    product?.name ||
    ""
  ).trim();

  if (!brand || !model) {
    console.error(
      "Produkti nuk ka brand/model:",
      product
    );
    return;
  }

  if (originalKeyword.trim() && product?.id) {
    const visitorId = getVisitorId();

    fetch(`${API_BASE_URL}/api/search-logs/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        visitor_id: visitorId,
        search_term: originalKeyword.trim(),
        product_id: product.id,
      }),
    }).catch((error) => {
      console.error(
        "Search product tracking error:",
        error
      );
    });
  }

  navigate(
    `/product/${encodeURIComponent(
      brand
    )}/${encodeURIComponent(model)}`
  );
};
const openPart = (part) => {
  const partId =
    part?.items?.[0]?.id || part?.id;

  if (!partId) return;

  // TRACK SEARCH - pa bllokuar navigimin
  if (originalKeyword.trim()) {
    const visitorId = getVisitorId();

    fetch(`${API_BASE_URL}/api/search-logs/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        visitor_id: visitorId,
        search_term: originalKeyword.trim(),

        // Është pjesë, jo produkt.
        // Mos e ruajmë si product_id.
        product_id: null,
      }),
    }).catch((error) => {
      console.error(
        "Part search tracking error:",
        error
      );
    });
  }

  // NAVIGIMI BËHET MENJËHERË
  navigate(`/parts/details/${partId}`);
};

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 md:px-6">
      <div className="mx-auto w-full max-w-[1500px]">
        {/* HEADER */}
        <header className="mb-10">
          <span className="text-xs font-extrabold uppercase tracking-[0.18em] text-blue-600">
            MASTER SOL&amp;TECH
          </span>

          <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-950 md:text-4xl">
            {text.title}
          </h1>

          <p className="mt-3 text-sm text-slate-500 md:text-base">
            {text.resultsFor}{" "}
            <strong className="text-slate-900">
              “{originalKeyword}”
            </strong>
          </p>

          <div className="mt-4 inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-extrabold text-blue-700">
            {totalResults} {text.results}
          </div>
        </header>

        {/* =================================================
            PRODUCTS
        ================================================= */}

        {productResults.length > 0 && (
          <section>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-black text-slate-900 md:text-2xl">
                {text.products}
              </h2>

              <span className="rounded-full bg-white px-3 py-1.5 text-xs font-extrabold text-slate-600 shadow-sm">
                {productResults.length}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {productResults.map((product) => (
                <article
                  key={product.id}
                  onClick={() => openProduct(product)}
                  className="
                    group
                    flex
                    cursor-pointer
                    flex-col
                    overflow-hidden
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    shadow-sm
                    transition
                    duration-300
                    hover:-translate-y-1
                    hover:border-blue-200
                    hover:shadow-xl
                  "
                >
                  {/* IMAGE */}
                  <div className="flex h-40 items-center justify-center bg-slate-50 p-4 md:h-48">
                    <img
                      src={getProductImage(product)}
                      alt={
                        product.name ||
                        text.unknownProduct
                      }
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.onerror =
                          null;

                        event.currentTarget.src =
                          FALLBACK_IMAGE;
                      }}
                      className="h-full w-full object-contain transition duration-300 group-hover:scale-105"
                    />
                  </div>

                  {/* CONTENT */}
                  <div className="flex flex-1 flex-col p-4">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-blue-600">
                      {product.brand || "-"}
                    </span>

                    <h3 className="mt-2 line-clamp-2 text-sm font-extrabold text-slate-900">
                      {product.name ||
                        text.unknownProduct}
                    </h3>

                    <p className="mt-2 text-xs text-slate-500">
                      {[
                        product.model,
                        product.category,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>

                    <p className="mt-auto pt-4 text-lg font-black text-green-600">
                      €
                      {Number(
                        product.price || 0
                      ).toFixed(2)}
                    </p>

                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openProduct(product);
                      }}
                      className="mt-3 w-full rounded-xl bg-blue-600 px-3 py-2.5 text-xs font-extrabold text-white transition hover:bg-blue-700"
                    >
                      {text.viewProduct}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* =================================================
            PARTS
        ================================================= */}

        {partResults.length > 0 && (
          <section
            className={
              productResults.length > 0
                ? "mt-14"
                : ""
            }
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-black text-slate-900 md:text-2xl">
                {text.parts}
              </h2>

              <span className="rounded-full bg-white px-3 py-1.5 text-xs font-extrabold text-slate-600 shadow-sm">
                {partResults.length}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {partResults.map((part) => (
                <article
                  key={part.id}
                  onClick={() => openPart(part)}
                  className="
                    group
                    flex
                    cursor-pointer
                    flex-col
                    overflow-hidden
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    shadow-sm
                    transition
                    duration-300
                    hover:-translate-y-1
                    hover:border-violet-200
                    hover:shadow-xl
                  "
                >
                  {/* IMAGE */}
                  <div className="flex h-40 items-center justify-center bg-slate-50 p-4 md:h-48">
                    <img
                      src={getPartImage(part)}
                      alt={
                        part.name ||
                        text.unknownPart
                      }
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.onerror =
                          null;

                        event.currentTarget.src =
                          FALLBACK_IMAGE;
                      }}
                      className="h-full w-full object-contain transition duration-300 group-hover:scale-105"
                    />
                  </div>

                  {/* CONTENT */}
                  <div className="flex flex-1 flex-col p-4">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-violet-600">
                      {part.category || text.parts}
                    </span>

                    <h3 className="mt-2 line-clamp-2 text-sm font-extrabold text-slate-900">
                      {part.name ||
                        text.unknownPart}
                    </h3>

                    <p className="mt-2 text-xs text-slate-500">
                      {[part.brand, part.model]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>

                    <p className="mt-auto pt-4 text-lg font-black text-green-600">
                      €
                      {Number(
                        part.price || 0
                      ).toFixed(2)}
                    </p>

                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openPart(part);
                      }}
                      className="mt-3 w-full rounded-xl bg-violet-600 px-3 py-2.5 text-xs font-extrabold text-white transition hover:bg-violet-700"
                    >
                      {text.viewPart}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* =================================================
            NO RESULTS
        ================================================= */}

        {keyword && totalResults === 0 && (
          <div className="rounded-3xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-xl text-slate-500">
              <i className="fas fa-search" />
            </div>

            <h2 className="mt-5 text-xl font-black text-slate-900">
              {text.noResults}
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              “{originalKeyword}”
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

export default Search;